import {
  collection,
  getDocs,
  doc,
  setDoc,
  serverTimestamp,
  query,
  limit as fsLimit,
} from 'firebase/firestore';
import { db } from '../../../firebase-config';
import { SCHOOL_SCHEMAS_COLLECTION } from './schoolSchemaPaths';
import { AI_PIPELINE_SCHEMA_V2 } from '../../../constants/aiPipelineModes';

function anonymiseValue(fieldId, entry) {
  if (!entry || entry.value === undefined || entry.value === null) return null;
  // Soft / narrative-ish fields excluded from hard harvest by default
  if (String(fieldId).startsWith('demo.inclusion_narrative')) return null;
  if (entry.provenance?.source === 'sef_narrative') return null;
  const v = entry.value;
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && /^\d+(\.\d+)?$/.test(v.trim())) return Number(v.trim());
  if (typeof v === 'string' && v.length > 80) return '[text]';
  if (typeof v === 'object') return '[object]';
  return v;
}

/**
 * Build anonymised cross-school field fill harvest for research / benchmarking prep.
 * Writes schemaHarvest/latest (staff-only). Does not change per-school schemas.
 */
export async function harvestSchoolSchemas(options = {}) {
  const max = Math.min(Math.max(Number(options.max) || 300, 1), 500);
  const snap = await getDocs(query(collection(db, SCHOOL_SCHEMAS_COLLECTION), fsLimit(max)));

  const fieldStats = {};
  let schoolCount = 0;

  snap.forEach((d) => {
    const data = d.data() || {};
    const fields = data.fields || {};
    schoolCount += 1;
    Object.entries(fields).forEach(([id, entry]) => {
      if (!fieldStats[id]) {
        fieldStats[id] = { populated: 0, sources: {}, sampleValues: [] };
      }
      fieldStats[id].populated += 1;
      const src = entry?.provenance?.source || 'unknown';
      fieldStats[id].sources[src] = (fieldStats[id].sources[src] || 0) + 1;
      const anon = anonymiseValue(id, entry);
      if (anon !== null && fieldStats[id].sampleValues.length < 5) {
        fieldStats[id].sampleValues.push(anon);
      }
    });
  });

  const payload = {
    pipeline: AI_PIPELINE_SCHEMA_V2,
    schoolCount,
    fieldCount: Object.keys(fieldStats).length,
    fields: fieldStats,
    note: 'Anonymised fill harvest. Soft/narrative fields excluded from sample values. Small-n rules apply before external use.',
    harvestedAt: serverTimestamp(),
    harvestedBy: options.actorEmail || options.actorUid || 'staff',
  };

  await setDoc(doc(db, 'schemaHarvest', 'latest'), payload, { merge: false });
  return {
    schoolCount,
    fieldCount: payload.fieldCount,
    path: 'schemaHarvest/latest',
  };
}